import type { Bookmark } from "../types";

/**
 * The rules behind a contact's Bookmarks section, kept pure so they are tested without
 * React. The platform is never stored; it is read from the URL's host each time.
 * LCATOMICZO-83.
 */

export type BookmarkPlatform = "linkedin" | "gmail" | "whatsapp" | "other";

/** Display order of the groups. */
export const BOOKMARK_PLATFORMS: BookmarkPlatform[] = [
  "linkedin",
  "gmail",
  "whatsapp",
  "other",
];

// A host matches a domain when it is the domain or a subdomain of it, so
// `www.linkedin.com` is LinkedIn and `notlinkedin.com` is not.
const PLATFORM_DOMAINS: Record<Exclude<BookmarkPlatform, "other">, string[]> = {
  linkedin: ["linkedin.com", "lnkd.in"],
  gmail: ["mail.google.com", "gmail.com"],
  whatsapp: ["wa.me", "whatsapp.com"],
};

const hostMatches = (host: string, domain: string) =>
  host === domain || host.endsWith(`.${domain}`);

const parseHttpUrl = (value: string): URL | null => {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (!url.hostname) return null;
  return url;
};

/**
 * Whether a stored URL may become an `href`. Only http and https: the column is also
 * written directly by Claude, and its database check covers shape, not scheme, so a
 * `javascript:` or `data:` URL must never reach a link.
 */
export const isOpenableUrl = (url: string): boolean =>
  parseHttpUrl(url) !== null;

/**
 * Cleans what someone pasted into the Add form. Adds `https://` when there is no scheme,
 * and returns null for anything that is not an http(s) URL with a host.
 */
export const normalizeBookmarkUrl = (input: string): string | null => {
  let trimmed = input.trim();
  // A URL has no spaces; browsers would otherwise percent-encode a typed sentence into
  // a "host".
  if (!trimmed || /\s/.test(trimmed)) return null;
  // The same link pasted twice end to end still parses, with the second copy inside the
  // fragment (prod contact 3415 has one). No real URL is its own second half.
  const half = trimmed.length / 2;
  if (
    Number.isInteger(half) &&
    trimmed.slice(0, half) === trimmed.slice(half)
  ) {
    trimmed = trimmed.slice(0, half);
  }
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed);
  const candidate = hasScheme
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, "")}`;
  const parsed = parseHttpUrl(candidate);
  // A real site has a dot in its host; this also refuses `https://` typed alone.
  if (!parsed || !parsed.hostname.includes(".")) return null;
  return parsed.toString();
};

// Only digits, spaces, brackets, dots and dashes, with at most one leading `+`.
const PHONE_SHAPE = /^\+?[\d\s().-]+$/;

/**
 * A phone number typed into the Add form, as a WhatsApp link. WhatsApp has no link to
 * copy, so `wa.me/<digits>` is built here. `wa.me` reads the leading digits as the
 * country code, so a number without one is refused rather than guessed: `(415) 293-3313`
 * would become `wa.me/4152933313`, a Swiss number. Returns null when the input is not
 * shaped like a phone at all. LCATOMICZO-83 phase 4.
 */
const whatsAppUrlFromPhone = (
  input: string,
): { url: string } | { error: "no_country_code" } | null => {
  // The UK writes a trunk zero inside international numbers, `+44 (0)7700 900123`, and
  // wa.me rejects a zero after the country code.
  const trimmed = input.trim().replaceAll("(0)", "");
  if (!PHONE_SHAPE.test(trimmed)) return null;
  const digits = trimmed.replace(/\D/g, "");
  const international = trimmed.startsWith("+")
    ? digits
    : trimmed.startsWith("00")
      ? digits.slice(2)
      : null;
  // E.164 caps a number at 15 digits; fewer than 7 is a typo, not a phone.
  const length = (international ?? digits).length;
  if (length < 7 || length > 15) return null;
  if (international === null) return { error: "no_country_code" };
  // No country code starts with 0, so `+00…` is a mistyped prefix, not a number.
  if (international.startsWith("0")) return null;
  return { url: new URL(`https://wa.me/${international}`).toString() };
};

/**
 * What the Add form's field holds: a link to save, a reason it cannot be saved, or null
 * when it is blank. A phone number is checked first, since most phone shapes would
 * otherwise fail as a web link with the wrong message.
 */
export const resolveBookmarkInput = (
  input: string,
): { url: string } | { error: "invalid" | "no_country_code" } | null => {
  if (!input.trim()) return null;
  const phone = whatsAppUrlFromPhone(input);
  if (phone) return phone;
  const url = normalizeBookmarkUrl(input);
  return url ? { url } : { error: "invalid" };
};

export const bookmarkPlatform = (url: string): BookmarkPlatform => {
  const parsed = parseHttpUrl(url);
  if (!parsed) return "other";
  const host = parsed.hostname.toLowerCase();
  for (const platform of BOOKMARK_PLATFORMS) {
    if (platform === "other") continue;
    if (PLATFORM_DOMAINS[platform].some((d) => hostMatches(host, d))) {
      return platform;
    }
  }
  return "other";
};

/** A starting name for a pasted URL. The user can change it before saving. */
export const suggestBookmarkLabel = (url: string): string => {
  const parsed = parseHttpUrl(url);
  if (!parsed) return "";
  switch (bookmarkPlatform(url)) {
    case "linkedin":
      if (parsed.pathname.startsWith("/messaging/")) return "Message thread";
      if (parsed.pathname.startsWith("/in/")) return "Profile";
      return "LinkedIn";
    case "gmail":
      return parsed.hash.startsWith("#search/")
        ? "Email search"
        : "Email thread";
    case "whatsapp":
      return "Chat";
    default:
      return parsed.hostname.replace(/^www\./, "");
  }
};

/**
 * The `href` for a bookmark, or null when it must not be a link. Gmail thread links are
 * pointed at the signed-in user's own mailbox: Gmail picks an account by sign-in order
 * (`u/0`, `u/1`), so a link saved on one machine can open the wrong one. `authuser` names
 * the account instead. Only `mail.google.com` is rewritten, and only here; the stored URL
 * never changes, so every viewer gets their own mailbox.
 */
export const bookmarkHref = (
  url: string,
  viewerEmail?: string | null,
): string | null => {
  const parsed = parseHttpUrl(url);
  if (!parsed) return null;
  if (!viewerEmail || parsed.hostname.toLowerCase() !== "mail.google.com") {
    return parsed.toString();
  }
  return `https://mail.google.com/mail/u/?authuser=${encodeURIComponent(
    viewerEmail,
  )}${parsed.hash}`;
};

/** A short host line shown under the label, e.g. `linkedin.com/in/ada`. */
export const bookmarkHostLine = (url: string): string => {
  const parsed = parseHttpUrl(url);
  if (!parsed) return url;
  const path = parsed.pathname === "/" ? "" : parsed.pathname;
  return `${parsed.hostname.replace(/^www\./, "")}${path}`;
};

// Second levels under a country code that are registries, not sites: `bbc.co.uk`,
// `abc.net.au`. Not the full public-suffix list, which is too big to ship for naming a
// group; a host it misses comes out one label short.
const COUNTRY_SECOND_LEVELS = new Set([
  "co",
  "com",
  "org",
  "net",
  "ac",
  "gov",
  "edu",
  "ne",
  "or",
  "go",
  "gob",
  "mil",
]);

/**
 * The site a link belongs to, for grouping links outside LinkedIn, Gmail and WhatsApp:
 * `community.avid.com` and `www.avid.com` are both `avid.com`. Null for anything with no
 * site to name: not http(s), an IP address, or a single-label host.
 */
export const bookmarkSite = (url: string): string | null => {
  const parsed = parseHttpUrl(url);
  if (!parsed) return null;
  const host = parsed.hostname.toLowerCase().replace(/\.$/, "");
  // The parser writes IPv6 in brackets and IPv4 as four numbers.
  if (host.startsWith("[") || /^\d+(\.\d+){3}$/.test(host)) return null;
  const labels = host.split(".");
  if (labels.length < 2) return null;
  const last = labels[labels.length - 1];
  const second = labels[labels.length - 2];
  const take =
    labels.length > 2 && last.length === 2 && COUNTRY_SECOND_LEVELS.has(second)
      ? 3
      : 2;
  return labels.slice(-take).join(".");
};

/**
 * `key` is the platform, `site:<domain>` for a site group, or `other` for what has no
 * site. `site` is set only on a site group.
 */
export type BookmarkGroup = {
  key: string;
  platform: BookmarkPlatform;
  site: string | null;
  bookmarks: Bookmark[];
};

// Sites sit between WhatsApp and Other.
const GROUP_RANK: Record<BookmarkPlatform | "site", number> = {
  linkedin: 0,
  gmail: 1,
  whatsapp: 2,
  site: 3,
  other: 4,
};

const groupRank = (group: BookmarkGroup) =>
  GROUP_RANK[group.site ? "site" : group.platform];

/**
 * Non-empty groups: LinkedIn, Gmail and WhatsApp, then one group per site A to Z, then
 * Other. Each alphabetical by label, ignoring case.
 */
export const groupBookmarks = (bookmarks: Bookmark[]): BookmarkGroup[] => {
  const groups = new Map<string, BookmarkGroup>();
  for (const bookmark of bookmarks) {
    const platform = bookmarkPlatform(bookmark.url);
    const site = platform === "other" ? bookmarkSite(bookmark.url) : null;
    const key =
      platform !== "other" ? platform : site ? `site:${site}` : "other";
    const group = groups.get(key) ?? { key, platform, site, bookmarks: [] };
    group.bookmarks.push(bookmark);
    groups.set(key, group);
  }
  return [...groups.values()]
    .sort(
      (a, b) =>
        groupRank(a) - groupRank(b) ||
        (a.site ?? "").localeCompare(b.site ?? ""),
    )
    .map((group) => ({
      ...group,
      bookmarks: group.bookmarks.sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base" }),
      ),
    }));
};

/** The existing bookmark with the same URL, ignoring surrounding spaces, if any. */
export const findDuplicateBookmark = (
  bookmarks: Bookmark[],
  url: string,
): Bookmark | undefined => bookmarks.find((b) => b.url.trim() === url.trim());

/**
 * Bookmarks for a company imported from an Atomic CRM JSON export, which may still carry
 * the old `context_links` (plain URL strings). Each valid link becomes a bookmark named
 * the way the Add form would name it; anything else, and repeats, are dropped.
 */
export const contextLinksToBookmarks = (links: unknown): Bookmark[] => {
  if (!Array.isArray(links)) return [];
  const bookmarks: Bookmark[] = [];
  for (const link of links) {
    if (typeof link !== "string") continue;
    const url = normalizeBookmarkUrl(link);
    if (!url || findDuplicateBookmark(bookmarks, url)) continue;
    bookmarks.push({ label: suggestBookmarkLabel(url), url });
  }
  return bookmarks;
};
